import { useEffect, useState } from 'react';
import {
  Badge, Button, Card, Col, DatePicker, Drawer, Empty, Form, Input, Modal, Popconfirm,
  Row, Select, Space, Spin, Table, Tag, Typography, message,
} from 'antd';
import { PlusOutlined, EditOutlined, UserOutlined, CheckSquareOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api/client';
import { useAuth } from '../store/auth';

const { Text, Title } = Typography;

/**
 * «Гости» — панель управления внешними пользователями (role_level >= 3).
 * Создание гостя (ФИО/email/срок) + выдача доступов (задачи/чаты/папки),
 * редактирование, отзыв грантов, продление.
 */
export default function GuestsPage() {
  const { user } = useAuth();
  const [guests, setGuests] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editGuest, setEditGuest] = useState(null);
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();
  const [tasks, setTasks] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/guests')
      .then(r => setGuests(r.data))
      .catch(e => message.error(e.response?.data?.detail || 'Ошибка загрузки'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    // задачи для выбора грантов (поиск по названию)
    api.get('/tasks', { params: { limit: 200 } })
      .then(r => setTasks(r.data || []))
      .catch(() => {});
  }, []);

  if ((user?.role_level ?? 1) < 3) {
    return (
      <div className="page-container">
        <Empty description="Доступно директорам и администраторам" />
      </div>
    );
  }

  const submitCreate = async (values) => {
    setSaving(true);
    try {
      const grants = [];
      (values.tasks || []).forEach(id => grants.push({ resource_type: 'task', resource_id: String(id), permission: 'view' }));
      if (values.grant_type) grants.push({ resource_type: values.grant_type, resource_id: values.grant_resource_id || '', permission: 'view' });
      const res = await api.post('/guests', {
        full_name: values.full_name,
        email: values.email,
        expires_at: values.expires_at?.format('YYYY-MM-DD') || null,
        grants,
      });
      const g = res.data.guest;
      Modal.success({
        title: 'Гость создан',
        content: (
          <div>
            <p><b>{g.full_name}</b> ({g.username})</p>
            <p>Временный пароль (передайте лично):</p>
            <p><Text code copyable>{g.temp_password}</Text></p>
            <p style={{ fontSize: 12, color: '#94a3b8' }}>
              При первом входе через sso.spzvg.ru гость сменит пароль.
            </p>
          </div>
        ),
      });
      setCreateOpen(false);
      createForm.resetFields();
      load();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Не удалось создать гостя');
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async (values) => {
    setSaving(true);
    try {
      const payload = {
        full_name: values.full_name,
        email: values.email,
        extend_all_until: values.extend_all_until?.format('YYYY-MM-DD') || null,
        add_grants: (values.add_tasks || []).map(id => ({
          resource_type: 'task', resource_id: String(id), permission: 'view',
        })),
      };
      await api.patch(`/guests/${editGuest.id}`, payload);
      message.success('Сохранено');
      setEditGuest(null);
      load();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (guestId, grantId) => {
    try {
      await api.patch(`/guests/${guestId}`, { revoke_grant_ids: [grantId] });
      message.success('Доступ отозван');
      load();
    } catch (e) {
      message.error('Не удалось отозвать');
    }
  };

  const columns = [
    {
      title: 'Гость', key: 'name',
      render: (_, g) => (
        <Space>
          <Badge count={g.grants.filter(x => !x.revoked && !isExpired(x)).length} size="small" color="geekblue">
            <UserOutlined style={{ fontSize: 22, color: '#94a3b8' }} />
          </Badge>
          <div>
            <div style={{ fontWeight: 600 }}>{g.full_name}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{g.email} · {g.username}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Доступы', key: 'grants',
      render: (_, g) => {
        const active = g.grants.filter(x => !x.revoked);
        if (!active.length) return <Text type="secondary">нет</Text>;
        return (
          <Space wrap size={4}>
            {active.map(gr => {
              const expired = isExpired(gr);
              return (
                <Tag
                  key={gr.id}
                  color={expired ? 'red' : gr.resource_type === 'task' ? 'geekblue' : 'green'}
                  closable
                  onClose={(e) => { e.preventDefault(); revoke(g.id, gr.id); }}
                  title={gr.revoked ? 'отозван' : expired ? 'истёк' : gr.expires_at ? `до ${fmt(gr.expires_at)}` : 'бессрочно'}
                >
                  {gr.resource_type === 'task'
                    ? (gr.task?.title || `Задача #${gr.resource_id}`)
                    : gr.resource_type === 'chat' ? '💬 чат' : `📁 ${gr.resource_id}`}
                  {expired ? ' (истёк)' : ''}
                </Tag>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: 'Действия', key: 'actions', width: 160,
      render: (_, g) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => {
            setEditGuest(g);
            form.setFieldsValue({
              full_name: g.full_name,
              email: g.email,
              add_tasks: [],
              extend_all_until: null,
            });
          }}>
            Изменить
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Гости</h1>
          <p className="page-subtitle">Внешние пользователи с точечными правами (задачи, чаты, папки)</p>
        </div>
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
          title="Добавить внешнего пользователя"
          style={{ width: 56, height: 56, fontSize: 22 }}
        />
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>
      ) : !guests?.length ? (
        <Empty description="Гостей пока нет — нажмите круглую кнопку + справа вверху" />
      ) : (
        <Card>
          <Table
            dataSource={guests}
            columns={columns}
            rowKey="id"
            pagination={false}
          />
        </Card>
      )}

      {/* Создание гостя */}
      <Drawer
        title="Добавить внешнего пользователя"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        width={480}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" onFinish={submitCreate}>
          <Form.Item name="full_name" label="ФИО" rules={[{ required: true }]}>
            <Input placeholder="Иванов Иван Иванович" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input placeholder="ivanov@company.ru" />
          </Form.Item>
          <Form.Item name="expires_at" label="Срок доступа (необязательно)">
            <DatePicker style={{ width: '100%' }} placeholder="бессрочно" />
          </Form.Item>
          <Form.Item name="tasks" label="Задачи — доступ (можно несколько)">
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              placeholder="Выберите задачи"
              options={tasks.map(t => ({ value: t.id, label: `#${t.id} ${t.title}` }))}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={saving} icon={<CheckSquareOutlined />}>
            Создать гостя и выдать доступы
          </Button>
        </Form>
      </Drawer>

      {/* Редактирование */}
      <Drawer
        title={`Гость: ${editGuest?.full_name || ''}`}
        open={!!editGuest}
        onClose={() => setEditGuest(null)}
        width={480}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={submitEdit}>
          <Form.Item name="full_name" label="ФИО" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="add_tasks" label="Добавить задачи">
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              placeholder="Выберите задачи"
              options={tasks.map(t => ({ value: t.id, label: `#${t.id} ${t.title}` }))}
            />
          </Form.Item>
          <Form.Item name="extend_all_until" label="Продлить все доступы до">
            <DatePicker style={{ width: '100%' }} placeholder="без изменений" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={saving}>
            Сохранить
          </Button>
        </Form>
      </Drawer>
    </div>
  );
}

const isExpired = (g) => g.expires_at && new Date(g.expires_at) < new Date();
const fmt = (d) => new Date(d).toLocaleDateString('ru-RU');
